import React, { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { t } from 'translation'
import LoadingButton from 'components/ui/LoadingButton/LoadingButton'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { connect } from 'react-redux'
import AuthErrorBox from 'components/Auth/AuthErrorBox'
import { authFail, resetAuthError } from 'store/actions'
import { inviteApi } from 'api'

const FORM_INPUTS = [
  {
    id: 'user_prescriber_code',
    name: 'prescriberCode',
    type: 'text',
    labelText: 'PRESCRIBER_CODE',
  },
  {
    id: 'user_email_address',
    name: 'email',
    type: 'email',
    labelText: 'EMAIL',
  },
]

const signupSchema = Yup.object().shape({
  email: Yup.string().email('INVALID_EMAIL').required('EMAIL_IS_REQUIRED'),
  prescriberCode: Yup.string()
    .required('PRESCRIBER_CODE_IS_REQUIRED')
    .matches(/^[A-Z0-9]{6,10}$/, 'PRESCRIBER_CODE_MUST_CONTAIN'),
})

const PrescriberCodeStepTwo = ({
  authFail,
  authError,
  resetAuthError,
  prescriberCodeParam,
  signupWithoutPrescriberCode,
}) => {
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const refs = {
    email: useRef(),
    prescriberCode: useRef(),
  }

  const prescriberCodeHandler = (value, setFieldValue) =>
    /^[A-Z0-9]{0,10}$/i.test(value) &&
    setFieldValue('prescriberCode', value.toUpperCase())

  return (
    <Formik
      initialValues={{ email: '', prescriberCode: prescriberCodeParam || '' }}
      validationSchema={signupSchema}
      onSubmit={async ({ email, prescriberCode }) => {
        resetAuthError()
        setIsLoading(true)

        const { error } = await inviteApi.sendSelfInvite({ email, prescriberCode })

        setIsLoading(false)

        if (error) return authFail(error)

        setSuccess(true)

        refs.prescriberCode.current.blur()
        refs.email.current.blur()
      }}
    >
      {({
        errors,
        setFieldValue,
        handleChange,
        submitForm,
        status,
        setStatus,
        touched,
        isValid,
      }) => (
        <Form className="auth_block offset_mod">
          <h1 className="section_title v4_mod color_mod center_mod offset_v4_mod">
            {!signupWithoutPrescriberCode
              ? t('signup.SIGNING_UP')
              : t('signup.SIGNING_UP_NO_PRESCRIBER_CODE')}
          </h1>
          <div className="auth_block_descr offset_mod center_lvl_mod">
            {!signupWithoutPrescriberCode
              ? t('signup.SIGNING_UP_DESCR_BENEFIT')
              : t('signup.SIGNING_NO_PRESCRIBER_CODE')}
          </div>
          {!success && (
            <AuthErrorBox
              formFields={['prescriberCode', 'email']}
              formTouched={touched}
              formErrors={errors}
              apiResponseError={authError || ''}
              resetApiResponseError={resetAuthError}
              clickedSubmit={status === 'clicked'}
            />
          )}
          {success && isValid ? (
            <div className="form_success">{t('auth.PLEASE_CHECK_YOUR_EMAIL')}</div>
          ) : null}
          <div className="auth_block_in">
            <div className="auth_block_input_w_list offset_v1_mod">
              <ul className="auth_block_input_list">
                {!signupWithoutPrescriberCode && (
                  <li
                    className={`auth_block_input_list_item`}
                    key={'user_prescriber_code'}
                  >
                    <dl className="form_cell form_cell_v1_mod">
                      <dt className="form_cell_title form_v3_mod">
                        <label htmlFor={'user_prescriber_code'}>
                          {t(`signup.PRESCRIBER_CODE`)}
                        </label>
                      </dt>
                      <dd className="form_field_wrap form_v3_mod">
                        <Field
                          className={`form_field v4_mod ${
                            status === 'clicked' && errors[name] ? 'error' : ''
                          }`}
                          type={'text'}
                          name={'prescriberCode'}
                          id={'user_prescriber_code'}
                          disabled={isLoading || success || signupWithoutPrescriberCode}
                          onChange={(event) => (
                            resetAuthError(),
                            prescriberCodeHandler(event.target.value, setFieldValue)
                          )}
                          readOnly={success || signupWithoutPrescriberCode}
                          innerRef={refs['prescriberCode']}
                        />
                      </dd>
                    </dl>
                  </li>
                )}
                <li className={`auth_block_input_list_item`} key={'user_email_address'}>
                  <dl className="form_cell form_cell_v1_mod">
                    <dt className="form_cell_title form_v3_mod">
                      <label htmlFor={'user_email_address'}>{t(`signup.EMAIL`)}</label>
                    </dt>
                    <dd className="form_field_wrap form_v3_mod">
                      <Field
                        className={`form_field v4_mod ${
                          status === 'clicked' && errors[name] ? 'error' : ''
                        }`}
                        type={'email'}
                        name={'email'}
                        id={'user_email_address'}
                        disabled={isLoading || success}
                        onChange={(event) => (resetAuthError(), handleChange(event))}
                        readOnly={success}
                        innerRef={refs['email']}
                      />
                    </dd>
                  </dl>
                </li>
              </ul>
            </div>
            <div className="btn_wrap center_lvl_mod">
              <LoadingButton
                className="btn_base_v1 offset_mod"
                isLoading={isLoading}
                onClick={() => {
                  setStatus('clicked')
                  submitForm()
                }}
                disabled={success}
              >
                {t('signup.CONTINUE')}
              </LoadingButton>
              <span className="auth_block_member_link">
                {t('signup.ALREADY_MEMBER')}
                {isLoading ? (
                  <span>{t('signup.CONNECT')}</span>
                ) : (
                  <Link to="/login">{t('signup.CONNECT')}</Link>
                )}
              </span>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}

const mapStateToProps = ({ auth: { error } }) => ({ authError: error })

const willMapDispatchToProps = {
  authFail,
  resetAuthError,
}

export default connect(mapStateToProps, willMapDispatchToProps)(PrescriberCodeStepTwo)
